/**
 * Duel detail — admin drill-down for one 1-v-1 match.
 *
 * Bound to `GET /admin/duels/{id}` (extended in the integrity-signals
 * PR to carry per-run identity, stake split, powerup usage, category
 * name, and pre-rolled `integrity_aggregates` at both the duel and per-
 * run levels).
 *
 * The page renders TWO side-by-side player cards (creator on the left,
 * opponent on the right when there is one) with:
 *   - identity chip + score/correct/ms header
 *   - stake breakdown (cash + bonus)
 *   - full integrity signal strip
 *   - per-answer table showing pick vs correct + per-answer telemetry
 *
 * Nothing on this page mutates duel state, stakes, rake, or the ledger
 * — the admin-duel-detail endpoint enforces read-only, and this page
 * has no verdict / lock / refund actions. It exists purely to visualise
 * client-integrity signals alongside outcome so a reviewer can spot
 * bot-runs.
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import { errorState, escape, fmtInt, fmtKobo, fmtRelTime } from './common.js';
import { integrityChips, perAnswerSignalsHtml } from './integrity.js';

export async function renderDuelDetail(host, ctx) {
  if (!ctx.duelId) {
    host.innerHTML = errorState('Missing duel id.');
    return;
  }
  host.innerHTML = `
    <a href="/admin/v2/duels" data-link class="adm2-btn"
       style="display:inline-flex;align-items:center;gap:8px;margin-bottom:18px;text-decoration:none;">
      ‹ All duels
    </a>
    <div class="adm2-skl" style="height:220px;width:100%;border-radius:14px;"></div>
  `;
  let d;
  try {
    d = await api(`/admin/duels/${encodeURIComponent(ctx.duelId)}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    if (err instanceof ApiError && err.status === 404) {
      host.innerHTML = `<div class="adm2-error">Duel not found.</div>
        <a href="/admin/v2/duels" data-link class="adm2-btn" style="margin-top:12px;display:inline-block;">← Back to duels</a>`;
      return;
    }
    toast.fromError ? toast.fromError(err) : toast.error(err.message || 'Failed to load duel.');
    host.innerHTML = errorState(err);
    return;
  }
  host.innerHTML = _renderPage(d);
}

function _renderPage(d) {
  const runs = Array.isArray(d.runs) ? d.runs : [];
  // Order runs by seat so creator (0) is always on the left.
  runs.sort((a, b) => Number(a.seat) - Number(b.seat));
  const winnerId = d.winner_id;
  const questions = Array.isArray(d.questions) ? d.questions : [];

  return `
    <a href="/admin/v2/duels" data-link class="adm2-btn"
       style="display:inline-flex;align-items:center;gap:8px;margin-bottom:18px;text-decoration:none;">
      ‹ All duels
    </a>

    ${_headerHtml(d)}

    <div class="adm2-section-label">Client-integrity aggregates (whole duel)</div>
    <div class="adm2-panel" style="padding:16px;">
      ${integrityChips(d.integrity_aggregates, { compact: false })}
      ${_wholeDuelWarningHtml(d.integrity_aggregates)}
    </div>

    <div class="adm2-section-label">Runs</div>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(360px,1fr));gap:16px;">
      ${runs.length
        ? runs.map((r) => _runCardHtml(r, { winnerId, questions })).join('')
        : '<div class="adm2-panel" style="padding:24px;text-align:center;color:var(--a-faint);">This duel has no runs on record.</div>'
      }
    </div>
  `;
}

function _headerHtml(d) {
  const creator = d.runs?.find((r) => r.user_id === d.creator_id) || null;
  const opponent = d.runs?.find((r) => r.user_id === d.opponent_id) || null;
  const creatorLabel = _identityLabel(creator, d.creator_id, 'Creator');
  const opponentLabel = d.opponent_id ? _identityLabel(opponent, d.opponent_id, 'Opponent') : '<span class="adm2-chip" style="background:var(--a-panel-2);color:var(--a-muted);border-color:var(--a-border);">Open — no opponent</span>';
  return `
    <div class="adm2-panel" style="padding:20px;margin-bottom:16px;">
      <div style="display:flex;flex-wrap:wrap;gap:12px;align-items:center;margin-bottom:12px;">
        <span class="adm2-chip" style="background:var(--a-brand-soft);color:var(--a-brand);border-color:transparent;">${escape(d.type || 'duel')}</span>
        <span class="adm2-chip" style="background:var(--a-panel-2);color:var(--a-muted);">${escape(d.status || '')}</span>
        ${d.category_name ? `<span class="adm2-chip" style="background:var(--a-panel-2);color:var(--a-muted);">${escape(d.category_name)}</span>` : ''}
        <span style="font:400 12px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-left:auto;">${escape(d.id || '')}</span>
      </div>
      <div style="display:flex;flex-wrap:wrap;align-items:center;gap:10px;font:500 16px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">
        <span>${creatorLabel}</span>
        <span style="color:var(--a-muted);">vs</span>
        <span>${opponentLabel}</span>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-top:16px;">
        ${_stat('Stake (each)', fmtKobo(d.stake_kobo))}
        ${_stat('Stake source', d.stake_source || '—')}
        ${_stat('Rake', `${(Number(d.rake_bps || 0) / 100).toFixed(1)}%`)}
        ${_stat('Winner', _winnerLabel(d))}
        ${_stat('Created', fmtRelTime(d.created_at))}
        ${_stat('Settled', d.settled_at ? fmtRelTime(d.settled_at) : '—')}
      </div>
    </div>
  `;
}

function _wholeDuelWarningHtml(agg) {
  if (!agg) return '';
  const any = agg.any_untrusted || agg.decoy_hit || agg.overlay_detected;
  if (!any) return '';
  return `
    <div style="margin-top:12px;padding:12px 14px;background:rgba(248,113,113,.10);border-left:3px solid #F87171;border-radius:6px;font-size:12.5px;color:var(--a-ink);line-height:1.55;">
      <b style="color:#F87171;">Bot-tell warning.</b>
      One or more integrity signals fired across this duel's answers.
      A winning run paired with <span style="font-family:'IBM Plex Mono',monospace;">is_trusted=false</span> /
      <span style="font-family:'IBM Plex Mono',monospace;">decoy_hit</span> / an overlay is stronger
      evidence than the score alone. Cross-check the per-run breakdown below.
    </div>
  `;
}

function _identityLabel(run, userId, fallback) {
  if (!run) return `<span class="adm2-chip" style="background:var(--a-panel-2);color:var(--a-muted);">${escape(fallback)} · ${escape((userId || '').slice(0, 8))}</span>`;
  const name = run.user_username || (run.user_email || '').split('@')[0] || fallback;
  return `<a href="/admin/v2/players/${encodeURIComponent(userId)}" data-link style="color:var(--a-brand);text-decoration:none;">${escape(name)}</a>`;
}

function _winnerLabel(d) {
  if (!d.winner_id) return d.status === 'completed' ? 'Tie' : '—';
  if (d.winner_id === d.creator_id) {
    const r = d.runs?.find((r) => r.user_id === d.creator_id);
    return r ? (r.user_username || (r.user_email || '').split('@')[0] || 'Creator') : 'Creator';
  }
  if (d.winner_id === d.opponent_id) {
    const r = d.runs?.find((r) => r.user_id === d.opponent_id);
    return r ? (r.user_username || (r.user_email || '').split('@')[0] || 'Opponent') : 'Opponent';
  }
  return '—';
}

function _stat(label, value) {
  return `
    <div style="background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:10px;padding:12px 14px;">
      <div style="font:600 10px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-faint);">${escape(label)}</div>
      <div style="font:600 15px 'IBM Plex Mono',monospace;color:var(--a-ink);margin-top:4px;">${escape(String(value))}</div>
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Per-run card
// ---------------------------------------------------------------------------

function _runCardHtml(run, { winnerId, questions }) {
  const isWinner = winnerId && run.user_id === winnerId;
  const name = run.user_username || (run.user_email || '').split('@')[0] || (run.user_id || '').slice(0, 8);
  const winnerChip = isWinner
    ? '<span class="adm2-chip" style="background:rgba(233,199,126,.14);color:#C9A24B;border-color:rgba(233,199,126,.4);">WINNER</span>'
    : '';
  return `
    <div class="adm2-panel" style="padding:16px;">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:12px;flex-wrap:wrap;">
        <a href="/admin/v2/players/${escape(run.user_id)}" data-link
           style="font:600 15px 'IBM Plex Sans',sans-serif;color:var(--a-brand);text-decoration:none;">
          ${escape(name)}
        </a>
        <span class="adm2-chip" style="background:var(--a-panel-2);color:var(--a-muted);">${escape(run.status || '')}</span>
        <span class="adm2-chip" style="background:var(--a-panel-2);color:var(--a-muted);">seat ${escape(String(run.seat))}</span>
        ${winnerChip}
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:8px;margin-bottom:14px;">
        ${_stat('Score', fmtInt(run.score))}
        ${_stat('Correct', `${fmtInt(run.correct)}/${fmtInt(run.answered)}`)}
        ${_stat('Total ms', fmtInt(run.total_ms))}
        ${_stat('50/50 uses', fmtInt((run.fiftyfifty_used_orders || []).length))}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px;">
        ${_stat('Stake cash', fmtKobo(run.stake_cash_kobo))}
        ${_stat('Stake bonus', fmtKobo(run.stake_bonus_kobo))}
      </div>
      <div class="adm2-section-label" style="margin-top:0;">Integrity signals</div>
      ${integrityChips(run.integrity_aggregates, { compact: false })}

      <div class="adm2-section-label" style="margin-top:16px;">Per-answer detail</div>
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${(run.answers || []).map((a) => _answerRowHtml(a, questions)).join('')
          || '<div style="color:var(--a-faint);font-size:12px;">No answers on this run.</div>'}
      </div>
    </div>
  `;
}

function _answerRowHtml(a, questions) {
  const q = questions.find((qq) => qq.order === a.order) || null;
  const picked = a.selected_index == null ? '—' : String.fromCharCode(65 + Number(a.selected_index));
  const correctL = q ? String.fromCharCode(65 + Number(q.answer_index)) : '—';
  const good = !!a.is_correct;
  return `
    <div style="display:flex;align-items:flex-start;gap:10px;padding:9px 12px;background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:8px;">
      <span style="font-family:'IBM Plex Mono',monospace;font-size:11px;color:var(--a-muted);min-width:24px;">Q${a.order + 1}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:12.5px;color:var(--a-ink);line-height:1.4;overflow-wrap:anywhere;">${escape((q && q.prompt) || '')}</div>
        <div style="font-size:11px;color:var(--a-muted);margin-top:3px;font-family:'IBM Plex Mono',monospace;">
          picked <b style="color:${good ? '#4ADE80' : '#F87171'};">${escape(picked)}</b>
          · correct <b style="color:#4ADE80;">${escape(correctL)}</b>
          · ${fmtInt(a.elapsed_ms)}ms
          · +${fmtInt(a.score_awarded)} pts
        </div>
        <div style="margin-top:5px;">${perAnswerSignalsHtml(a.client_signals)}</div>
      </div>
    </div>
  `;
}
